/**
 * Search Return Stash
 * ====================
 * A tiny sessionStorage breadcrumb written right before the search bar sends
 * the user off to a result page (product card, "see full page", alternate
 * pill, created page, kit). On the way back — usually a plain browser Back,
 * which is a same-document `popstate` for every soft-nav exit and gives us
 * no reliable "the user just came back" signal — the search page consults
 * this stash to know which persisted chat transcript (see `chat-history.ts`)
 * to restore.
 *
 * Deliberately NOT cleared on consume: forward-then-back-again should still
 * restore the same conversation. It IS cleared on an explicit cancel, so a
 * user who says "start over" doesn't get resurrected by a stray Back later.
 */

const STORAGE_KEY = 'iai-search-return';

/** How recent a stash must be to still be trusted (shared with the resume-on-back window). */
export const SEARCH_RETURN_WINDOW_MS = 60 * 60 * 1000; // 1 hour

interface SearchReturnStash {
  transcriptId: string;
  ts: number;
}

export function writeSearchReturnStash(transcriptId: string): void {
  if (typeof window === 'undefined') return;
  try {
    const stash: SearchReturnStash = { transcriptId, ts: Date.now() };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stash));
  } catch (e) {
    console.warn('Failed to write search return stash:', e);
  }
}

export function readSearchReturnStash(maxAgeMs: number): SearchReturnStash | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const stash = JSON.parse(raw) as SearchReturnStash;
    if (!stash?.transcriptId || typeof stash.ts !== 'number') return null;
    if (Date.now() - stash.ts > maxAgeMs) return null;
    return stash;
  } catch (e) {
    console.warn('Failed to read search return stash:', e);
    return null;
  }
}

/**
 * Whether the search page's `?q=` is this conversation's own. Back from a
 * result page lands on a `?q=` the conversation wrote itself (its first words,
 * a refined query, a later message). A new search arriving with other words
 * (the extension's side panel, another store's search link, the SearchAction)
 * must run, not resurrect the conversation the stash points at.
 */
export function queryBelongsToTranscript(
  query: string,
  transcript: {
    initialUserInput?: string;
    finalSearchQuery?: string;
    turns: Array<{ userInput?: string; parsed: { searchQuery?: string } }>;
  },
): boolean {
  const normalize = (text?: string) => (text ?? '').normalize('NFC').replace(/\s+/g, ' ').trim().toLowerCase();
  const wanted = normalize(query);
  if (!wanted) return false;
  const own = [
    transcript.initialUserInput,
    transcript.finalSearchQuery,
    ...transcript.turns.flatMap((turn) => [turn.userInput, turn.parsed.searchQuery]),
  ];
  return own.some((text) => normalize(text) === wanted);
}

export function clearSearchReturnStash(): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    console.warn('Failed to clear search return stash:', e);
  }
}
